import { utils } from '@/utils';
import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import { ChannelApiResponse } from '@/types/endpoints';
import type { WriteRequestOptions } from '../requestOptions';

export interface EnsureProductFieldsRequest {
  mapping_ids: number[];
}

export interface CreatedField {
  mapping_id: number;
  name: string;
  type: string;
}

export interface FieldError {
  mapping_id: number;
  name: string;
  error: string;
}

export interface EnsureProductFieldsResponse {
  created: CreatedField[];
  errors: FieldError[];
}

type EnsureProductFieldsApiResponse = Partial<EnsureProductFieldsResponse> & {
  data?: EnsureProductFieldsResponse;
};

type RequestParams = {
  subscriptionId: number | string;
  mappingIds: number[];
};

export const ensureProductFields = async (
  scope: RequestScope,
  { subscriptionId, mappingIds }: RequestParams,
  options: WriteRequestOptions
): Promise<ChannelApiResponse<EnsureProductFieldsResponse>> => {
  try {
    if (!subscriptionId) {
      throw new Error('Subscription ID is required');
    }
    if (!mappingIds || mappingIds.length === 0) {
      throw new Error('At least one mapping ID is required');
    }

    const requestBody: EnsureProductFieldsRequest = {
      mapping_ids: mappingIds,
    };

    const result = await scopedChannelApi(scope).post<EnsureProductFieldsApiResponse>('astur/integrations/pt/ensure-product-fields', requestBody, {
      ...options,
      headers: {
        'x-astur-subscription-id': String(subscriptionId),
      },
    });
    // Normalize rather than reject: the write has already happened
    // server-side, so a backend that omits an empty `created`/`errors` array
    // must not surface a completed operation as a failure.
    const response = (result.data?.data ?? result.data) as Partial<EnsureProductFieldsResponse> | null | undefined;
    if (!response || typeof response !== 'object') {
      throw new Error('Invalid ensure product fields response');
    }

    return {
      data: {
        created: Array.isArray(response.created) ? response.created : [],
        errors: Array.isArray(response.errors) ? response.errors : [],
      },
      status: result.status,
    };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};
